import React, {
    useCallback,
    useRef,
    useState,
    useMemo,
} from 'react';
import DraggableTable from 'components/DraggableTable/DraggableTable';
import { useNavigate } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from 'contexts/AuthContext';
import useToast from 'hooks/useToast';
import { Deal } from '../types';
import { getDealTableColumns } from './helper';
import PageSizeDropdown from './PageSizeDropdown/PageSizeDropdown';
import PaginationButtons from './PaginationButtons/PaginationButtons';
import CreateGroupModal from './BulkActions/CreateGroupModal';
import AssociateDealsToGroupModal from './BulkActions/AssociateDealsToGroupModal';
import DeleteDealModal from './BulkActions/DeleteDealModal';
import { UnitType } from '../constants';
import { useDissociateDealsFromGroup } from '../hooks/useDissociateDealsFromGroup';

interface IDealsTableProps {
    deals: Deal[];
    visibleColumns: Record<string, boolean>;
    className?: string;
    pageSize: number;
    currentPage: number;
    totalItems: number;
    selectedDeal?: Deal;
    hoveredDeal?: Deal | null;
    setPageSize: (size: number) => void;
    setCurrentPage: (page: number) => void;
    handleSelectDeal?: (deal: Deal) => void;
    onRowMouseEnter?: (deal: Deal) => void;
    onRowMouseLeave?: () => void;
    onAscendingSort: (columnName: string) => void;
    onDescendingSort: (columnName: string) => void;
    columnOrder?: string[];
    onColumnOrderChange?: (newOrder: string[]) => void;
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    selectedUnitType: UnitType;
    handleSelectUnitType: (unitType: UnitType) => void;
    selectedDealGroup?: number;
    dealGroupName?: string;
}

const DealsTable: React.FC<IDealsTableProps> = ({
    deals,
    visibleColumns,
    className,
    pageSize,
    currentPage,
    totalItems,
    selectedDeal,
    hoveredDeal,
    setPageSize,
    setCurrentPage,
    onAscendingSort,
    onDescendingSort,
    handleSelectDeal = _ => {},
    onRowMouseEnter,
    onRowMouseLeave,
    columnOrder,
    onColumnOrderChange,
    sorting,
    setSorting,
    selectedUnitType,
    handleSelectUnitType,
    selectedDealGroup,
    dealGroupName,
}) => {
    const [openCreateGroupModal, setOpenCreateGroupModal] = useState(false);
    const [openAssociateDealsToGroupModal, setOpenAssociateDealsToGroupModal] = useState(false);
    const [openDeleteDealModal, setOpenDeleteDealModal] = useState(false);
    const [selectedDeals, setSelectedDeals] = useState<Deal[]>([]);
    const allColumns = useMemo(() => getDealTableColumns(selectedUnitType), [selectedUnitType]);
    const tableRef = useRef<{ resetRowSelection:() => void }>(null);

    const navigate = useNavigate();
    const { selectedOrganizationId } = useAuthContext();
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();
    const { mutate: dissociateDealsFromGroup } = useDissociateDealsFromGroup();

    const handleAscendingSortPressed = (columnName: string | number | symbol) => {
        onAscendingSort(columnName as string);
    };

    const handleDescendingSortPressed = (columnName: string | number | symbol) => {
        onDescendingSort(columnName as string);
    };

    const handleRowClick = useCallback(
        (rowData: Deal) => {
            handleSelectDeal(rowData);
        },
        [handleSelectDeal],
    );

    const toggleOpenCreateGroupModal = () => setOpenCreateGroupModal(!openCreateGroupModal);
    const toggleOpenAssociateDealsToGroupModal = () => setOpenAssociateDealsToGroupModal(!openAssociateDealsToGroupModal);
    const toggleDeleteDealModal = () => setOpenDeleteDealModal(!openDeleteDealModal);
    const handleDealGroupsActionsSuccess = () => tableRef.current?.resetRowSelection();

    const handleRemoveFromGroup = (selectedRows: Deal[]) => {
        if (selectedDealGroup == null || !selectedOrganizationId) return;

        dissociateDealsFromGroup(
            {
                orgId: selectedOrganizationId,
                postData: {
                    associations: selectedRows.map(deal => ({
                        dealId: Number(deal.id),
                        dealGroupId: selectedDealGroup,
                    })),
                },
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ['deals', selectedOrganizationId] });
                    handleDealGroupsActionsSuccess();
                    showSuccess(`Deals successfully removed from ${dealGroupName}!`);
                },
                onError: error => {
                    showError(error?.message || 'Failed to remove deals from group. Please try again.');
                },
            },
        );
    };

    return (
        <div className={className}>
            <DraggableTable
                data={deals}
                ref={tableRef}
                columns={allColumns}
                columnVisibility={visibleColumns}
                selectedItem={selectedDeal}
                hoveredItem={hoveredDeal ?? undefined}
                onRowMouseEnter={onRowMouseEnter}
                onRowMouseLeave={onRowMouseLeave}
                onAscendingSortPressed={handleAscendingSortPressed}
                onDescendingSortPressed={handleDescendingSortPressed}
                initialColumnOrder={columnOrder}
                onColumnOrderChange={onColumnOrderChange}
                onAddToExistingGroup={(selectedRows: Deal[]) => {
                    setSelectedDeals(selectedRows);
                    toggleOpenAssociateDealsToGroupModal();
                }}
                onCreateGroupFromSelection={(selectedRows: Deal[]) => {
                    setSelectedDeals(selectedRows);
                    toggleOpenCreateGroupModal();
                }}
                onRemoveFromGroup={handleRemoveFromGroup}
                onCreateReport={(selectedRows: Deal[]) => {
                    const baseUrl = '/reports';
                    const params = new URLSearchParams();
                    params.set('create', 'true');

                    const selectedDealIds = selectedRows.map(deal => deal.id);
                    if (selectedDealIds && selectedDealIds.length > 0) {
                        params.set('dealIds', selectedDealIds.join(','));
                    }

                    navigate(`${baseUrl}?${params.toString()}`);
                }}
                onDelete={(selectedRows: Deal[]) => {
                    setSelectedDeals(selectedRows);
                    toggleDeleteDealModal();
                }}
                onRowClick={handleRowClick}
                sorting={sorting}
                setSorting={setSorting}
                selectedUnitType={selectedUnitType}
                handleSelectUnitType={handleSelectUnitType}
                selectedDealGroup={selectedDealGroup}
                dealGroupName={dealGroupName}
            />
            <div className="d-flex justify-content-between align-items-center pt-2">
                <PageSizeDropdown pageSize={pageSize} onPageSizeChange={setPageSize} pageSizeOptions={[5, 10, 20, 50]} />
                <PaginationButtons
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalItems / pageSize)}
                    onPageChange={setCurrentPage}
                    itemsPerPage={pageSize}
                    totalItems={totalItems}
                />
            </div>
            <CreateGroupModal
                toggle={toggleOpenCreateGroupModal}
                deals={selectedDeals}
                isOpen={openCreateGroupModal}
                onCreationSuccess={handleDealGroupsActionsSuccess}
            />
            <AssociateDealsToGroupModal
                toggle={toggleOpenAssociateDealsToGroupModal}
                deals={selectedDeals}
                isOpen={openAssociateDealsToGroupModal}
                onAsociationSuccess={handleDealGroupsActionsSuccess}
            />
            <DeleteDealModal
                toggle={toggleDeleteDealModal}
                isOpen={openDeleteDealModal}
                deals={selectedDeals}
                onDeleteSuccess={handleDealGroupsActionsSuccess}
            />
        </div>
    );
};

export default DealsTable;
